import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axios";
import {
  clearAuthSlice,
  fetchingUserDetails,
  setImpersonate,
  setIsAuthenticated,
  setNotification,
  setUserDetails,
  setUsersList,
  setUsersListLoading,
} from "../slices/userSlice";
import { hideLoader, showLoader } from "../slices/loaderSlice";
import { showToaster } from "../slices/toastSlice";
import { DisplayErrorMessages } from "../../utils/helper";
import DataStorage from "../../utils/DataStorage";

const setCookie = (dispatch, data) => {
  const { accessToken, refreshToken, user } = data;
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("login", "true");
  dispatch(setIsAuthenticated(true));
  dispatch(
    setNotification({
      isNotification: data.user.paymentNotificationEnabled,
    })
  );
  dispatch(setUserDetails(user));
  dispatch(hideLoader());
};

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (
    { email, password, keepMeLoggedin, setIsForceLogout, setForceLogoutEmail },
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.post("auth/login", {
        email,
        password,
        keepMeLoggedin,
        isCrimeRisk: true,
      });
      setCookie(thunkAPI.dispatch, response.data.data);
      return response.data;
    } catch (err) {
      // Handle errors appropriately
      const isForceLogout = err?.response?.data?.data?.forceLogout
        ? true
        : false;
      const error = DisplayErrorMessages(err);
      setIsForceLogout(isForceLogout);
      setForceLogoutEmail(email);
      if (!isForceLogout)
        thunkAPI.dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: error ?? "Please contact the admin",
            showDialog: true,
            timer: "5000",
          })
        );
      thunkAPI.dispatch(hideLoader());
      const errorMessage = err.response?.data?.message || err.message;
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const setPassword = createAsyncThunk(
  "user/set-password",
  async ({ code, password, resendLink }, thunkAPI) => {
    try {
      const response = await axiosInstance.post("auth/set-password", {
        code,
        password,
        resendLink,
        isCrimeRisk: true,
      });

      thunkAPI.dispatch(
        showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: response.data.message ?? "Successfully",
          showDialog: true,
          timer: "5000",
        })
      );
      return response.data;
    } catch (err) {
      const error = DisplayErrorMessages(err);
      thunkAPI.dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: error ?? "Error",
          showDialog: true,
          timer: "5000",
        })
      );
      // Handle errors appropriately
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const resendActivationLink = createAsyncThunk(
  "user/resend-link",
  async ({ code, resendLink }, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        "auth/resend-activation-email",
        {
          code,
          resendLink,
          isCrimeRisk: true,
        }
      );

      thunkAPI.dispatch(
        showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: response.data.message ?? "Successfully",
          showDialog: true,
          timer: "5000",
        })
      );
      return response.data;
    } catch (err) {
      const error = DisplayErrorMessages(err);
      thunkAPI.dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: error ?? "Error",
          showDialog: true,
          timer: "5000",
        })
      );
      // Handle errors appropriately
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const signupUser = createAsyncThunk(
  "user/signupUser",
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosInstance.get("user/roles");
      const roleId = res.data.data.find((d) => d.name === "user")?.id;
      const response = await axiosInstance.post("user/sign-up", {
        ...body,
        role: roleId,
        isCrimeRisk: true,
      });
      dispatch(
        showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: response.data.message ?? "successfully",
          showDialog: true,
          timer: "5000",
        })
      );
      return response.data;
    } catch (err) {
      // Handle errors appropriately
      const error = DisplayErrorMessages(err);
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: error ? error : "Unauthorized",
          showDialog: true,
          timer: "5000",
        })
      );
      return rejectWithValue(err);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "user/logoutUser",
  async (_, { dispatch }) => {
    try {
      dispatch(setIsAuthenticated(false));
      dispatch(fetchingUserDetails(false));
      dispatch(clearAuthSlice());
      dispatch(
        showLoader({
          showLoader: true,
          loaderMessage: "Loading...",
          showBackDrop: true,
        })
      );
      // dispatch(resetMapState());
      await axiosInstance.post("auth/logout", { isCrimeRisk: true });
    } catch (err) {
      console.log("err", err);
    } finally {
      dispatch(showLoader(false));
      DataStorage.clear();
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "user/logoutUser",
  async ({ email, fromAdmin = false }, thunkAPI) => {
    try {
      const res = await axiosInstance.post("auth/forgotPassword", {
        email,
        fromAdmin,
        isCrimeRisk: true,
      });
      if (res.status === 200) {
        thunkAPI.dispatch(
          showToaster({
            dialogBgColor: "bg-success",
            dialogMsg: res.data.message,
            showDialog: true,
            timer: "5000",
          })
        );
      } else {
        thunkAPI.dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: res.data.message,
            showDialog: true,
            timer: "5000",
          })
        );
      }
      return res.data;
    } catch (err) {
      console.log("err", err);
      const error = DisplayErrorMessages(err);
      thunkAPI.dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: error ?? "Unauthorized",
          showDialog: true,
          timer: "5000",
        })
      );
      return thunkAPI.rejectWithValue(err);
    } finally {
      thunkAPI.dispatch(hideLoader());
    }
  }
);

export const fetchUserData = createAsyncThunk(
  "user/user",
  async (_, { dispatch }) => {
    try {
      const res = await axiosInstance.get("user/user");
      if (res && res.status === 200) {
        dispatch(setIsAuthenticated(true));
        dispatch(setUserDetails(res.data.data.user));
        dispatch(
          setNotification({
            isNotification: res.data.data.user.paymentNotificationEnabled,
          })
        );
        if (res.data.data.user.isImpersonate)
          dispatch(setImpersonate(res.data.data.user.isImpersonate));
      } else {
        dispatch(logoutUser());
        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: res.data.message,
            showDialog: true,
            timer: "5000",
          })
        );
        dispatch(setIsAuthenticated(false));
      }
      dispatch(fetchingUserDetails(false));
    } catch (error) {
      dispatch(logoutUser());
      console.log(error);
      dispatch(fetchingUserDetails(false));
    }
  }
);

export const fetchAllUsers = createAsyncThunk(
  "user/fetchAllUsers",
  async (_, { dispatch }) => {
    try {
      dispatch(setUsersListLoading(true));
      const res = await axiosInstance.get("user/list");
      if (res && res.status === 200) {
        // Extract the users array from the response
        const usersData = res.data.data?.users || res.data.data || [];
        dispatch(setUsersList(usersData));
        return usersData;
      } else {
        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: res.data.message || "Failed to fetch users",
            showDialog: true,
            timer: "5000",
          })
        );
        return [];
      }
    } catch (error) {
      console.log("Error fetching users:", error);
      const errorMsg = DisplayErrorMessages(error);
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: errorMsg || "Failed to fetch users",
          showDialog: true,
          timer: "5000",
        })
      );
      return [];
    } finally {
      dispatch(setUsersListLoading(false));
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post("user/update-user", {
        ...body,
        isCrimeRisk: true,
      });
      dispatch(
        showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: response.data.message ?? "User updated successfully",
          showDialog: true,
          timer: "5000",
        })
      );
      // Refresh the users list after update
      dispatch(fetchAllUsers());
      return response.data;
    } catch (err) {
      // Handle errors appropriately
      const error = DisplayErrorMessages(err);
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: error ? error : "Failed to update user",
          showDialog: true,
          timer: "5000",
        })
      );
      return rejectWithValue(err);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post("user/delete", {
        userId,
        isCrimeRisk: true,
      });
      dispatch(
        showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: response.data.message ?? "User deleted successfully",
          showDialog: true,
          timer: "5000",
        })
      );
      // Refresh the users list after delete
      dispatch(fetchAllUsers());
      return response.data;
    } catch (err) {
      // Handle errors appropriately
      const error = DisplayErrorMessages(err);
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: error ? error : "Failed to delete user",
          showDialog: true,
          timer: "5000",
        })
      );
      return rejectWithValue(err);
    }
  }
);
